/**
 * 处理页面的购物车功能
 */
class HalloweenCartManager {
  constructor() {
    this.isSubmitting = false;
    this.init();
  }

  /**
   * 初始化购物车管理器
   */
  init() {
    this.bindEvents();
  }

  /**
   * 绑定事件监听器
   */
  bindEvents() {
    // 使用事件委托处理所有购买按钮的点击事件
    document.addEventListener("click", (event) => {
      const buyButton = event.target.closest(
        ".sp26_deal-btn-primary",
      );

      if (buyButton) {
        // 防止事件冒泡和默认行为
        event.preventDefault();
        event.stopPropagation();

        // 检查是否已经在处理中
        if (buyButton.hasAttribute("data-processing")) {
          return;
        }

        this.handleBuyButtonClick(buyButton);
        return;
      }

      // 处理万圣节横幅背景图片点击事件
      const bannerImage = event.target.closest(
        ".spring2026_rewards__btn",
      );
      if (bannerImage) {
        event.preventDefault();
        this.handleBannerImageClick();
      }
    });
  }

  /**
   * 处理购买按钮点击事件
   * @param {HTMLElement} button - 被点击的按钮元素
   */
  async handleBuyButtonClick(button) {
    if (this.isSubmitting || button.hasAttribute("data-processing")) {
      return;
    }
    // console.log("点击购买按钮:", button);
    try {
      this.isSubmitting = true;
      button.setAttribute("data-processing", "true");
      this.setButtonLoading(button, true);

      // 获取商品ID
      const productIds = this.getProductIds(button);

      if (!productIds || productIds.length === 0) {
        throw new Error("未找到有效的商品ID");
      }

      // 添加商品到购物车
      await this.addProductsToCart(productIds);
      // 新窗口打开购物车页面
      // window.open("/cart", "_blank");
      window.location.href = "/cart";
      return false
      // 触发购物车更新事件
      this.triggerCartUpdate();
    } catch (error) {
      console.error("添加商品到购物车失败:", error);
    } finally {
      this.isSubmitting = false;
      button.removeAttribute("data-processing");
      this.setButtonLoading(button, false);
    }
  }

  /**
   * 获取商品ID
   * @param {HTMLElement} button - 按钮元素
   * @returns {Array} 商品ID数组
   */
  getProductIds(button) {
    const dataId = button.getAttribute("data-id");

    if (!dataId) {
      return [];
    }

    // 支持逗号分隔的多个ID（用于组合商品）
    return dataId
      .split(",")
      .map((id) => id.trim())
      .filter((id) => id);
  }

  /**
   * 添加商品到购物车
   * @param {Array} productIds - 商品ID数组
   */
  async addProductsToCart(productIds) {
    // 构建购物车项目数组
    const items = productIds.map((id) => ({
      id: id,
      quantity: 1,
    }));

    // 使用现有的购物车添加方法
    const response = await fetch("/cart/add.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Requested-With": "XMLHttpRequest",
      },
      body: JSON.stringify({ items }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.description || "添加商品失败");
    }

    return await response.json();
  }

  /**
   * 设置按钮加载状态
   * @param {HTMLElement} button - 按钮元素
   * @param {boolean} loading - 是否显示加载状态
   */
  setButtonLoading(button, loading) {
    if (loading) {
      button.setAttribute("aria-busy", "true");
      button.style.pointerEvents = "none";
      button.style.opacity = "0.7";
    } else {
      button.removeAttribute("aria-busy");
      button.style.pointerEvents = "";
      button.style.opacity = "";
    }
  }

  /**
   * 触发购物车更新事件
   */
  async triggerCartUpdate() {
    try {
      // 获取最新的购物车数据
      const cartResponse = await fetch("/cart.js");
      const cartData = await cartResponse.json();

      // 触发购物车变更事件
      document.documentElement.dispatchEvent(
        new CustomEvent("cart:change", {
          bubbles: true,
          detail: {
            baseEvent: "variant:add",
            cart: cartData,
          },
        }),
      );

      // 商品已成功添加到购物车
      this.showCartSuccessNotification();
      
    } catch (error) {
      console.error("更新购物车数据失败:", error);
    }
  }

  /**
   * 显示加购成功提示
   */
  showCartSuccessNotification() {
    // 检查万圣节成功提示管理器是否可用
    if (typeof showHalloweenCartSuccess === 'function') {
      showHalloweenCartSuccess({
        title: 'Check your cart now — your robot is ready!',
        message: '',
        duration: 3000
      });
    } else {
      // 降级处理：使用简单的alert提示
      // console.log('万圣节加购成功提示管理器未加载，使用降级提示');
      // 可以在这里添加其他降级提示方式
    }
  }

  /**
   * 处理横幅图片点击事件
   */
  handleBannerImageClick() {
    // 打开指定的链接
    window.SmileUI.openPanel();
  }
}

// 确保只初始化一次购物车管理器
let cartManager = null;

function initCartManager() {
  if (!cartManager) {
    cartManager = new HalloweenCartManager();
  }
}

/**
 * Fans Saying Video Autoplay Manager
 * 处理粉丝说部分的视频自动播放功能
 */
class FansSayingVideoManager {
  constructor() {
    this.videos = [];
    this.currentVideoIndex = 0;
    this.isPlaying = false;
    this.hasStarted = false;
    this.observer = null;
    this.sectionElement = null; // 缓存 DOM 元素
    this.eventHandlers = new Map(); // 存储事件处理器引用
    this.retryCount = 0; // 重试计数器
    this.maxRetries = 3; // 最大重试次数
    this.videoLoadTimeout = 10000; // 视频加载超时时间（可配置）
    this.init();
  }

  /**
   * 初始化视频管理器
   */
  init() {
    this.sectionElement = document.getElementById("fans-saying");
    if (!this.sectionElement) return;
    
    this.findVideos();
    this.setupIntersectionObserver();
    this.bindVideoEvents();
  }

  /**
   * 查找所有视频元素
   */
  findVideos() {
    if (!this.sectionElement) return;

    this.videos = Array.from(this.sectionElement.querySelectorAll("video"));
  }

  /**
   * 设置交叉观察器
   */
  setupIntersectionObserver() {
    if (!this.sectionElement) return;

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !this.hasStarted) {
            // 立即设置标志，防止竞态条件
            this.hasStarted = true;
            this.startVideoSequence();
          }
        });
      },
      {
        threshold: 0.3,
        rootMargin: "0px 0px -10% 0px",
      },
    );

    this.observer.observe(this.sectionElement);
  }

  /**
   * 绑定视频事件
   */
  bindVideoEvents() {
    this.videos.forEach((video, index) => {
      // 检查是否已经绑定，防止重复绑定
      if (video.dataset.eventsBound === 'true') return;
      
      // 确保视频静音
      video.muted = true;

      // 创建事件处理器（使用命名函数以便后续移除）
      const onEnded = () => this.onVideoEnded(index);
      const onError = (e) => {
        console.error(`视频 ${index + 1} 播放错误:`, e);
        this.onVideoError(index);
      };

      // 绑定事件
      video.addEventListener("ended", onEnded);
      video.addEventListener("error", onError);

      // 存储事件处理器引用，便于后续移除
      this.eventHandlers.set(video, { onEnded, onError });
      
      // 标记已绑定
      video.dataset.eventsBound = 'true';
    });
  }

  /**
   * 移除视频事件监听器
   */
  unbindVideoEvents() {
    this.videos.forEach((video) => {
      const handlers = this.eventHandlers.get(video);
      if (handlers) {
        video.removeEventListener("ended", handlers.onEnded);
        video.removeEventListener("error", handlers.onError);
        delete video.dataset.eventsBound;
      }
    });
    this.eventHandlers.clear();
  }
  /**
   * 等待所有视频加载完成
   */
  async waitForVideosToLoad() {

    const loadPromises = this.videos.map((video, index) => {
      return new Promise((resolve, reject) => {
        // 如果视频已经加载完成
        if (video.readyState >= 2) {
          resolve();
          return;
        }

        // 使用可配置的超时时间
        const timeout = setTimeout(() => {
          resolve();
        }, this.videoLoadTimeout);

        // 监听加载完成事件
        const onLoadedData = () => {
          clearTimeout(timeout);
          video.removeEventListener("loadeddata", onLoadedData);
          video.removeEventListener("canplay", onCanPlay);
          resolve();
        };

        // 监听可以播放事件
        const onCanPlay = () => {
          clearTimeout(timeout);
          video.removeEventListener("loadeddata", onLoadedData);
          video.removeEventListener("canplay", onCanPlay);
          resolve();
        };

        // 监听错误事件
        const onError = (error) => {
          console.warn(`视频 ${index + 1} 加载失败:`, error);
          clearTimeout(timeout);
          video.removeEventListener("loadeddata", onLoadedData);
          video.removeEventListener("canplay", onCanPlay);
          video.removeEventListener("error", onError);
          resolve(); // 即使出错也继续执行
        };

        video.addEventListener("loadeddata", onLoadedData);
        video.addEventListener("canplay", onCanPlay);
        video.addEventListener("error", onError);

        // 如果视频还没有开始加载，触发加载
        if (video.readyState === 0) {
          video.load();
        }
      });
    });

    // 等待所有视频加载完成
    await Promise.all(loadPromises);
  }

  /**
   * 开始视频播放序列
   */
  async startVideoSequence() {
    if (this.videos.length === 0) return;

    this.currentVideoIndex = 0;
    this.retryCount = 0; // 重置重试计数器

    try {
      // 等待所有视频加载完成
      await this.waitForVideosToLoad();
      
      // 设置播放状态（在实际开始播放后）
      this.isPlaying = true;
      
      // 播放第一个视频
      await this.playVideo(0);
    } catch (error) {
      console.error("视频序列启动失败:", error);
      this.isPlaying = false;
      this.hasStarted = false;
    }
  }

  /**
   * 播放指定索引的视频
   * @param {number} index - 视频索引
   */
  async playVideo(index) {
    if (index >= this.videos.length) {
      this.isPlaying = false;
      return;
    }

    const video = this.videos[index];
    if (!video) return;

    try {
      // 重置视频到开始位置
      video.currentTime = 0;

      // 播放视频
      await video.play();

      // 添加播放状态类
      video.classList.add("playing");
    } catch (error) {
      console.error(`播放视频 ${index + 1} 失败:`, error);
      this.onVideoError(index);
    }
  }

  /**
   * 视频播放结束处理
   * @param {number} index - 视频索引
   */
  onVideoEnded(index) {
    // 移除播放状态类（带边界检查）
    if (this.videos[index]) {
      this.videos[index].classList.remove("playing");
    }

    // 重置重试计数器
    this.retryCount = 0;

    // 检查是否是最后一个视频
    if (index === this.videos.length - 1) {
      this.currentVideoIndex = 0;
      this.playVideo(this.currentVideoIndex);
    } else {
      this.currentVideoIndex = index + 1;
      this.playVideo(this.currentVideoIndex);
    }
  }

  /**
   * 视频播放错误处理
   * @param {number} index - 视频索引
   */
  onVideoError(index) {
    // 移除播放状态类（带边界检查）
    if (this.videos[index]) {
      this.videos[index].classList.remove("playing");
    }

    // 检查重试次数，防止无限循环
    this.retryCount++;
    if (this.retryCount >= this.maxRetries) {
      console.error(`视频播放失败次数过多 (${this.retryCount}/${this.maxRetries})，停止播放`);
      this.isPlaying = false;
      return;
    }

    // 检查是否是最后一个视频
    if (index === this.videos.length - 1) {
      this.currentVideoIndex = 0;
      this.playVideo(this.currentVideoIndex);
    } else {
      this.currentVideoIndex = index + 1;
      this.playVideo(this.currentVideoIndex);
    }
  }

  /**
   * 停止所有视频播放
   */
  stopAllVideos() {
    this.videos.forEach((video) => {
      video.pause();
      video.currentTime = 0;
      video.classList.remove("playing");
    });

    this.isPlaying = false;
    this.currentVideoIndex = 0;
  }

  /**
   * 重置视频管理器
   */
  reset() {
    this.stopAllVideos();
    this.hasStarted = false;
    this.retryCount = 0;
  }

  /**
   * 销毁视频管理器
   */
  destroy() {
    // 断开交叉观察器
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    
    // 移除所有事件监听器
    this.unbindVideoEvents();
    
    // 停止所有视频
    this.stopAllVideos();
    
    // 清理引用
    this.videos = [];
    this.sectionElement = null;
  }
}

// 确保只初始化一次视频管理器
let videoManager = null;

function initVideoManager() {
  if (!videoManager) {
    videoManager = new FansSayingVideoManager();
  }
}

/**
 * Reviews Carousel Cloner
 * 复制评论轮播内容以实现无限滚动效果
 */
class ReviewsCarouselCloner {
  constructor() {
    this.init();
  }

  /**
   * 初始化轮播克隆器
   */
  init() {
    this.cloneCarouselContent();
  }

  /**
   * 查找所有评论轮播轨道并复制内容
   */
  cloneCarouselContent() {
    // 查找所有轮播轨道元素
    const tracks = document.querySelectorAll('[data-reviews-carousel-track]');
    
    if (tracks.length === 0) {
      console.log('未找到评论轮播轨道元素');
      return;
    }

    // 遍历每个轨道并复制内容
    tracks.forEach((track, index) => {
      this.cloneTrackContent(track, index);
    });

    // console.log(`已复制 ${tracks.length} 个评论轮播轨道的内容`);
  }

  /**
   * 复制单个轨道的内容
   * @param {HTMLElement} track - 轨道元素
   * @param {number} index - 轨道索引
   */
  cloneTrackContent(track, index) {
    try {
      // 获取轨道内的所有子元素
      const children = Array.from(track.children);
      
      if (children.length === 0) {
        console.log(`轨道 ${index + 1} 没有子元素`);
        return;
      }

      // 克隆每个子元素并添加到轨道末尾
      children.forEach((child) => {
        const clone = child.cloneNode(true);
        // 添加克隆标识
        clone.setAttribute('data-cloned', 'true');
        track.appendChild(clone);
      });

      // console.log(`轨道 ${index + 1}: 已复制 ${children.length} 个元素`);
    } catch (error) {
      console.error(`复制轨道 ${index + 1} 内容失败:`, error);
    }
  }

  /**
   * 移除所有克隆的元素（用于重置）
   */
  removeClonedContent() {
    const clonedElements = document.querySelectorAll('[data-cloned="true"]');
    clonedElements.forEach(element => element.remove());
    console.log(`已移除 ${clonedElements.length} 个克隆元素`);
  }

  /**
   * 重新克隆内容
   */
  refresh() {
    this.removeClonedContent();
    this.cloneCarouselContent();
  }
}

// 确保只初始化一次评论轮播克隆器
let carouselCloner = null;

function initCarouselCloner() {
  if (!carouselCloner) {
    carouselCloner = new ReviewsCarouselCloner();
  }
}

/**
 * Snowflake Effect Manager
 * 雪花特效管理器 - 创建并管理页面雪花动画效果
 */
class SnowflakeEffectManager {
  constructor(options = {}) {
    // 配置参数（支持外部自定义）
    this.config = {
      container: options.container || '.snowflake-box', // 容器选择器
      initialCount: options.initialCount || 50, // 初始雪花数量
      createInterval: options.createInterval || 300, // 创建间隔（毫秒）
      initialDelay: options.initialDelay || 100, // 初始批次延迟（毫秒）
      size: {
        min: options.sizeMin || 0.5,
        max: options.sizeMax || 2,
      },
      duration: {
        min: options.durationMin || 10,
        max: options.durationMax || 20,
      },
      delay: {
        min: options.delayMin || 0,
        max: options.delayMax || 5,
      },
      opacity: {
        min: options.opacityMin || 0.4,
        max: options.opacityMax || 1,
      },
      snowflakeChar: options.snowflakeChar || '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 12 11" fill="none"><path d="M3.40443 10.9592C3.23862 10.8814 3.20372 10.6478 3.31716 10.3969L3.64877 9.69608L3.21244 9.84315C3.12518 9.87776 2.95065 9.91237 2.81975 9.85181C2.74994 9.8172 2.70631 9.76529 2.68885 9.69608C2.64522 9.50574 2.8372 9.35001 3.04664 9.2808L3.77094 9.02989C3.88438 8.99529 4.03273 8.85686 4.08509 8.75304L4.32943 8.24259L3.45678 8.53675C3.29971 8.58866 3.15136 8.58866 3.05537 8.53675C2.99428 8.50214 2.95065 8.45888 2.92447 8.38967C2.87211 8.22529 3.02046 8.05225 3.28225 7.96574L4.46033 7.56776C4.57377 7.53315 4.72212 7.40337 4.77448 7.2909L5.22826 6.33922C5.27189 6.2527 5.35043 6.02775 5.16717 5.94124C5.10609 5.91528 5.02755 5.89798 4.93156 5.90663L3.8582 5.99315C3.73603 6.0018 3.5615 6.09697 3.49169 6.18348L2.71503 7.12652C2.66267 7.18708 2.60159 7.24764 2.5405 7.28225C2.40961 7.36877 2.26998 7.34281 2.22635 7.32551H2.21762L2.20017 7.30821C2.14781 7.2736 2.12163 7.21304 2.11291 7.14382C2.09545 7.03135 2.15654 6.89292 2.26126 6.7545L2.82848 6.06236L2.26998 6.10562C2.14781 6.11427 1.97328 6.20944 1.89475 6.29596L1.42352 6.86697C1.30135 7.01405 1.10064 7.15248 0.934834 7.07461L0.917381 7.05731C0.865022 7.0227 0.830116 6.96214 0.812663 6.89292C0.786484 6.74585 0.891201 6.59011 0.961013 6.51225L1.24026 6.18348L0.489784 6.24405C0.376339 6.2527 0.262895 6.24405 0.175631 6.19214C0.0709129 6.14023 0.00110113 6.05371 0.00110113 5.95854C-0.0163518 5.77685 0.175631 5.62112 0.437425 5.60382L1.20535 5.54326L0.865022 5.24045C0.734125 5.12798 0.664313 5.00685 0.664313 4.89438C0.664313 4.82517 0.690492 4.76461 0.742851 4.72135C0.830116 4.64348 0.952287 4.64348 1.10064 4.71269C1.16172 4.73865 1.22281 4.78191 1.28389 4.83382L1.85984 5.33562C1.85984 5.33562 1.92092 5.37888 1.96456 5.40483C2.06055 5.44809 2.17399 5.47404 2.26126 5.47404L2.81975 5.43079L2.13036 4.82517C1.99946 4.71269 1.92092 4.58292 1.92092 4.47045C1.92092 4.40123 1.93838 4.34067 1.98201 4.29741C2.01692 4.25415 2.13036 4.17629 2.3398 4.27146C2.40088 4.29741 2.46197 4.34067 2.52305 4.39258L3.45678 5.21449C3.45678 5.21449 3.50914 5.25775 3.5615 5.28371C3.65749 5.32697 3.77094 5.35292 3.84947 5.35292L4.90538 5.2664C5.05373 5.25775 5.16717 5.19719 5.20208 5.11933C5.24571 5.03281 5.21953 4.92034 5.13227 4.79056L4.52141 3.89943C4.47778 3.83022 4.38179 3.76101 4.2858 3.71775C4.24217 3.70045 4.19853 3.68314 4.16363 3.67449L2.95937 3.4755C2.88084 3.46685 2.81102 3.44089 2.74994 3.41494C2.5405 3.31977 2.53178 3.17269 2.5405 3.12078C2.5405 3.06022 2.58414 2.99966 2.6365 2.96505C2.72376 2.89584 2.87211 2.87853 3.04664 2.90449L3.92801 3.05157L3.60513 2.58438C3.5615 2.51516 3.46551 2.44595 3.36952 2.40269C3.32589 2.38539 3.29098 2.36808 3.25607 2.35943L2.52305 2.22966C2.44451 2.21235 2.3747 2.19505 2.31362 2.16909C2.12163 2.07393 2.09545 1.9528 2.09545 1.88359C2.09545 1.81437 2.13036 1.75381 2.18272 1.71055C2.26998 1.64134 2.40961 1.61538 2.58414 1.64999L3.01173 1.72786L2.58414 1.10493C2.42706 0.87999 2.42706 0.646394 2.58414 0.533922C2.66267 0.482012 2.77612 0.47336 2.88084 0.52527C2.9681 0.568529 3.05537 0.646394 3.11645 0.741563L3.55278 1.37314L3.64004 0.923248C3.65749 0.82808 3.70985 0.646394 3.84075 0.568529C3.86693 0.551225 3.90183 0.542574 3.92801 0.533922C3.95419 0.533922 3.9891 0.533922 4.024 0.551225C4.10254 0.594484 4.19853 0.689653 4.21599 0.819428C4.22471 0.914597 4.21599 1.00111 4.20726 1.06168L4.05891 1.81437C4.03273 1.9355 4.07636 2.12584 4.14617 2.22966L4.46905 2.69685L4.65231 1.79707C4.68722 1.62404 4.75703 1.49426 4.85302 1.4337C4.8792 1.4164 4.90538 1.40774 4.94028 1.39909C5.00137 1.39044 5.05373 1.39909 5.06245 1.40774H5.07118C5.23698 1.48561 5.25444 1.71921 5.2108 1.90954L4.96646 3.12943C4.94028 3.25056 4.98392 3.44089 5.05373 3.53606L5.64713 4.40123C5.69949 4.4791 5.76057 4.53101 5.82166 4.56562C6.01364 4.65213 6.12708 4.45314 6.17072 4.36663L6.63322 3.39764C6.68558 3.28516 6.6943 3.09483 6.65067 2.98235L6.22308 1.84033C6.12708 1.58078 6.17072 1.36449 6.32779 1.29527C6.38888 1.26932 6.45869 1.26932 6.51978 1.29527C6.61577 1.34718 6.71176 1.45965 6.76412 1.61538L7.07827 2.44595L7.32261 1.9355C7.37497 1.82303 7.3837 1.63269 7.34006 1.52022L7.087 0.819428C7.00846 0.611787 7.00846 0.360888 7.18299 0.274371C7.24407 0.239764 7.32261 0.239764 7.39242 0.274371C7.52332 0.334933 7.60186 0.490663 7.62804 0.585832L7.77639 0.992462L8.09927 0.308978C8.21271 0.0667298 8.42215 -0.0543941 8.58795 0.0234713C8.75375 0.101337 8.78866 0.334933 8.67521 0.585832L8.34361 1.28662L8.77993 1.13954C8.8672 1.10493 9.04173 1.07033 9.17262 1.13089C9.24243 1.1655 9.28607 1.22606 9.30352 1.29527C9.34715 1.47696 9.15517 1.63269 8.93701 1.71055L8.21271 1.96145C8.09927 2.00471 7.95092 2.13449 7.89856 2.23831L7.65422 2.74876L8.52686 2.4546C8.68394 2.40269 8.83229 2.39404 8.92828 2.4546C8.98937 2.48921 9.04173 2.54112 9.05918 2.60168C9.11154 2.79202 8.91956 2.94775 8.70139 3.01696L7.52332 3.41494C7.40988 3.44955 7.26153 3.58797 7.20917 3.69179L6.75539 4.65213C6.70303 4.75595 6.6943 4.84247 6.71176 4.92034C6.72921 4.9809 6.76412 5.02416 6.8252 5.05876C6.88629 5.08472 6.96483 5.10202 7.06082 5.09337L8.1429 5.00685C8.25634 4.9982 8.4396 4.90303 8.50941 4.81652L9.28607 3.87348C9.33843 3.81292 9.39951 3.75236 9.4606 3.71775C9.59149 3.63123 9.73112 3.65719 9.77475 3.67449H9.78348L9.80093 3.69179C9.85329 3.7264 9.87947 3.78696 9.88819 3.85618C9.89692 3.96865 9.84456 4.10708 9.73984 4.2455L9.17262 4.93764L9.73112 4.89438C9.85329 4.88573 10.0278 4.79056 10.1064 4.70404L10.5776 4.13303C10.6299 4.07247 10.691 4.01191 10.7521 3.9773C10.8656 3.90809 11.0052 3.91674 11.0837 3.95134C11.1099 3.96865 11.1361 3.9773 11.1448 4.00326C11.171 4.03786 11.1797 4.07247 11.1884 4.10708C11.2059 4.21955 11.1535 4.34932 11.0401 4.48775L10.7608 4.81652L11.5113 4.75595C11.6248 4.7473 11.7382 4.75595 11.8255 4.80786C11.9302 4.85977 12 4.94629 12 5.04146C12 5.11067 11.9825 5.17989 11.9302 5.2318C11.8516 5.31831 11.712 5.37888 11.5549 5.39618L10.787 5.45674L11.1274 5.75955C11.2583 5.87202 11.3281 6.0018 11.3281 6.11427C11.3281 6.18348 11.3019 6.24405 11.2583 6.2873C11.171 6.36517 11.0401 6.36517 10.9005 6.30461C10.8394 6.27865 10.7783 6.23539 10.7172 6.18348L10.1413 5.67303C10.1413 5.67303 10.0802 5.62978 10.0365 5.61247C9.94055 5.56921 9.81838 5.54326 9.73984 5.54326L9.18135 5.58652L9.87074 6.19214C10.0016 6.30461 10.0802 6.43438 10.0802 6.54686C10.0802 6.60742 10.0627 6.67663 10.0191 6.71989C9.98419 6.76315 9.87074 6.84101 9.66131 6.74585C9.60022 6.71989 9.53913 6.67663 9.47805 6.62472L8.54432 5.80281C8.54432 5.80281 8.49196 5.75955 8.4396 5.7336C8.34361 5.69034 8.23016 5.66438 8.15163 5.66438L7.09572 5.7509C6.94737 5.75955 6.83393 5.82011 6.79902 5.89798C6.76412 5.97584 6.78157 6.09697 6.86883 6.22674L7.47969 7.11787C7.52332 7.18708 7.61931 7.2563 7.7153 7.29955C7.75893 7.31686 7.80257 7.33416 7.82875 7.34281L9.033 7.5418C9.11154 7.55045 9.18135 7.57641 9.24243 7.60236C9.45187 7.69753 9.4606 7.84461 9.45187 7.89652C9.45187 7.95708 9.40824 8.01765 9.35588 8.05225C9.26861 8.12147 9.12027 8.13877 8.94573 8.11281L8.06436 7.96574L8.38724 8.43293C8.43087 8.50214 8.52686 8.57135 8.62286 8.61461C8.66649 8.63192 8.70139 8.64922 8.7363 8.65787L9.46932 8.78765C9.54786 8.80495 9.61767 8.82225 9.67876 8.84821C9.87074 8.94338 9.89692 9.0645 9.89692 9.13372C9.89692 9.20293 9.86201 9.25484 9.80966 9.2981C9.72239 9.36731 9.57404 9.39327 9.40824 9.35866L8.98064 9.28945L9.40824 9.91237C9.47805 10.0075 9.51296 10.1114 9.52168 10.2065C9.53041 10.3277 9.48678 10.4228 9.40824 10.4834C9.3297 10.5353 9.22498 10.5439 9.12027 10.492C9.033 10.4488 8.94573 10.3796 8.88465 10.2757L8.44833 9.63551L8.36106 10.0854C8.32616 10.2584 8.25634 10.3796 8.16035 10.4401C8.09927 10.4747 8.02946 10.4834 7.96837 10.4661L7.95092 10.4574C7.78511 10.3796 7.76766 10.146 7.80257 9.94698L7.95092 9.19428C7.9771 9.08181 7.93346 8.88282 7.86365 8.78765L7.54077 8.32046L7.35752 9.22023C7.32261 9.38461 7.2528 9.52304 7.15681 9.5836C7.13063 9.60091 7.10445 9.60956 7.06954 9.61821C7.00846 9.62686 6.96483 9.61821 6.94737 9.60956H6.93865C6.77284 9.53169 6.75539 9.2981 6.79902 9.10776L7.04336 7.88787C7.06954 7.76675 7.02591 7.57641 6.9561 7.48124L6.35397 6.60742C6.30161 6.52955 6.24053 6.47764 6.17944 6.44304C5.99619 6.35652 5.87402 6.55551 5.83038 6.64202L5.36788 7.61102C5.31552 7.71484 5.3068 7.91383 5.35043 8.0263L5.77803 9.16832C5.85656 9.38461 5.85656 9.63551 5.67331 9.71338C5.61222 9.73934 5.54241 9.73934 5.48133 9.71338C5.38533 9.67012 5.28934 9.549 5.23698 9.39327L4.92283 8.55405L4.67849 9.0645C4.62613 9.16832 4.6174 9.37596 4.66104 9.47978L4.9141 10.1806C4.98392 10.3709 5.00137 10.6305 4.82684 10.7256C4.75703 10.7602 4.68722 10.7602 4.6174 10.7256C4.48651 10.6651 4.4167 10.5093 4.38179 10.4142L4.23344 10.0075L3.91056 10.691C3.79712 10.9333 3.58768 11.0544 3.42188 10.9765L3.40443 10.9592Z" fill="#F7DFAF"/></svg>', // 雪花字符（SVG）
      enabled: options.enabled !== false, // 是否启用
    };

    this.containerElement = null;
    this.intervalId = null;
    this.snowflakePool = []; // 雪花元素池（性能优化）
    this.isRunning = false;
    this.snowflakeCount = 0; // 当前活跃雪花数量

    if (this.config.enabled) {
      // 移动端优化：减少雪花数量
      if (window.innerWidth <= 720) {
        this.config.initialCount = 15;
        this.config.createInterval = 800;
        // console.log('Mobile mode: Reduced snowflake count');
      }
      this.init();
    }
  }

  /**
   * 初始化雪花特效
   */
  init() {
    this.containerElement = document.querySelector(this.config.container);

    if (!this.containerElement) {
      console.warn(`雪花特效容器未找到: ${this.config.container}`);
      return;
    }

    this.start();
  }

  /**
   * 启动雪花特效
   */
  start() {
    if (this.isRunning) return;

    this.isRunning = true;

    // 初始创建一批雪花（错开时间避免卡顿）
    this.createInitialSnowflakes();

    // 持续创建新雪花
    this.intervalId = setInterval(() => {
      this.createSnowflake();
    }, this.config.createInterval);
  }

  /**
   * 停止雪花特效
   */
  stop() {
    if (!this.isRunning) return;

    this.isRunning = false;

    // 清除定时器
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  /**
   * 暂停雪花特效（保留现有雪花）
   */
  pause() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    this.isRunning = false;
  }

  /**
   * 恢复雪花特效
   */
  resume() {
    if (this.isRunning) return;

    this.isRunning = true;
    this.intervalId = setInterval(() => {
      this.createSnowflake();
    }, this.config.createInterval);
  }

  /**
   * 创建初始批次雪花
   */
  createInitialSnowflakes() {
    for (let i = 0; i < this.config.initialCount; i++) {
      setTimeout(() => {
        this.createSnowflake();
      }, i * this.config.initialDelay);
    }
  }

  /**
   * 创建单个雪花
   */
  createSnowflake() {
    if (!this.containerElement) return;

    // 从对象池获取或创建新元素
    const snowflake = this.getSnowflakeElement();

    // 随机位置
    snowflake.style.left = this.getRandomValue(0, 100) + '%';

    // 随机大小
    const size = this.getRandomValue(
      this.config.size.min,
      this.config.size.max
    );
    snowflake.style.width = size + 'em';


    // 随机动画时长（控制下落速度）
    const duration = this.getRandomValue(
      this.config.duration.min,
      this.config.duration.max
    );
    snowflake.style.animationDuration = duration + 's';

    // 随机延迟
    const delay = this.getRandomValue(
      this.config.delay.min,
      this.config.delay.max
    );
    snowflake.style.animationDelay = delay + 's';

    // 随机透明度
    const opacity = this.getRandomValue(
      this.config.opacity.min,
      this.config.opacity.max
    );
    snowflake.style.opacity = opacity;

    // 添加到容器
    this.containerElement.appendChild(snowflake);
    this.snowflakeCount++;

    // 动画结束后回收元素
    const totalDuration = (duration + delay) * 1000;
    setTimeout(() => {
      this.recycleSnowflake(snowflake);
    }, totalDuration);
  }

  /**
   * 从对象池获取雪花元素（性能优化）
   * @returns {HTMLElement} 雪花元素
   */
  getSnowflakeElement() {
    // 如果对象池中有可用元素，直接复用
    if (this.snowflakePool.length > 0) {
      return this.snowflakePool.pop();
    }

    // 否则创建新元素
    const snowflake = document.createElement('div');
    snowflake.classList.add('snowflake');
    snowflake.innerHTML = this.config.snowflakeChar;
    return snowflake;
  }

  /**
   * 回收雪花元素到对象池（性能优化）
   * @param {HTMLElement} snowflake - 雪花元素
   */
  recycleSnowflake(snowflake) {
    if (!snowflake || !snowflake.parentNode) return;

    // 从 DOM 中移除
    snowflake.remove();
    this.snowflakeCount--;

    // 重置样式
    snowflake.style.left = '';
    snowflake.style.fontSize = '';
    snowflake.style.animationDuration = '';
    snowflake.style.animationDelay = '';
    snowflake.style.opacity = '';

    // 放入对象池（限制池大小，避免内存占用过大）
    if (this.snowflakePool.length < 100) {
      this.snowflakePool.push(snowflake);
    }
  }

  /**
   * 获取随机值
   * @param {number} min - 最小值
   * @param {number} max - 最大值
   * @returns {number} 随机值
   */
  getRandomValue(min, max) {
    return Math.random() * (max - min) + min;
  }

  /**
   * 清除所有雪花
   */
  clearAll() {
    if (!this.containerElement) return;

    const snowflakes = this.containerElement.querySelectorAll('.snowflake');
    snowflakes.forEach((snowflake) => snowflake.remove());

    this.snowflakeCount = 0;
    this.snowflakePool = [];
  }

  /**
   * 更新配置
   * @param {Object} newConfig - 新配置
   */
  updateConfig(newConfig) {
    this.config = { ...this.config, ...newConfig };

    // 如果正在运行，重启以应用新配置
    if (this.isRunning) {
      this.stop();
      this.start();
    }
  }

  /**
   * 获取当前状态
   * @returns {Object} 当前状态信息
   */
  getStatus() {
    return {
      isRunning: this.isRunning,
      snowflakeCount: this.snowflakeCount,
      poolSize: this.snowflakePool.length,
      config: this.config,
    };
  }

  /**
   * 销毁雪花特效管理器
   */
  destroy() {
    this.stop();
    this.clearAll();
    this.containerElement = null;
    this.snowflakePool = [];
  }
}

// 确保只初始化一次雪花特效管理器
let snowflakeManager = null;

function initSnowflakeEffect(options) {
  if (!snowflakeManager) {
    snowflakeManager = new SnowflakeEffectManager(options);
  }
  return snowflakeManager;
}

// 页面加载完成后初始化购物车管理器、视频管理器、轮播克隆器和雪花特效
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => {
    initCartManager();
    initVideoManager();
    initCarouselCloner();
    // 初始化雪花特效（可根据需要传入自定义配置）
    // initSnowflakeEffect({ initialCount: 50, createInterval: 300 });
  });
} else {
  initCartManager();
  initVideoManager();
  initCarouselCloner();
  // 初始化雪花特效（可根据需要传入自定义配置）
  // initSnowflakeEffect({ initialCount: 50, createInterval: 300 });
}
